/**
 * The frame loop: one callback per display frame (requestAnimationFrame), with the time
 * taken from the engine clock so that frames, input timestamps and the simulation share one
 * timebase. Hidden pages get no frames at all; the run then simply loses that time (see
 * LocalRun).
 *
 * On screens of 120 Hz and more (many Android phones) a FramePacer lets only about 60 frames a
 * second through: drawing, not the game code, is what a frame costs, so twice the frames
 * would cost twice the battery and heat for motion the kids would hardly see. The game plays
 * the same at any frame rate: input carries its own time and lands on its tick, and the
 * simulation steps by the clock, not by frames.
 */

export interface FrameScheduler {
  /** `time`: when the display frame began, if the scheduler knows (requestAnimationFrame). */
  request(callback: (time?: number) => void): number;
  cancel(handle: number): void;
}

export const animationFrames: FrameScheduler = {
  request: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => cancelAnimationFrame(handle),
};

/** Frame intervals the screen's refresh is measured over. */
const PACE_WINDOW = 32;
/**
 * Screens refreshing at shorter intervals than this (ms) get paced: 120 Hz and up, while 90 and
 * 100 Hz screens (whose measured interval wobbles around 10 ms) keep every frame...
 */
const FAST_SCREEN_MS = 9.5;
/** ...unless faster than any phone's (250 Hz): callbacks not tied to a screen (tests, tools). */
const FASTEST_SCREEN_MS = 4;
/** The frame rate fast screens are brought down to (about). */
const PACED_INTERVAL_MS = 1000 / 60;

/**
 * Decides which display frames are drawn. It measures the screen's refresh interval (the
 * median of the latest callback intervals, so the odd late frame does not count) and, on a
 * fast screen, draws every k-th refresh — the k closest to a 60 Hz frame, so 50–75 fps (120 and
 * 144 Hz: every second, 165 Hz: every third, 240 Hz: every fourth; rounding also absorbs the
 * few per cent the measured interval wobbles) — evenly paced: a frame is drawn once k − ½
 * refreshes have passed since the last drawn one, so the jitter of the callbacks never makes it
 * skip one too many or too few. 60 and 90 Hz screens draw every frame.
 */
export class FramePacer {
  private readonly intervals = new Float64Array(PACE_WINDOW);
  private recorded = 0;
  private last = Number.NaN;
  private lastDrawn = Number.NEGATIVE_INFINITY;
  /** The screen's refresh interval, ms (0 until measured). */
  private refresh = 0;
  /** Draw every this many refreshes. */
  private every = 1;

  /** A display frame at `now` (ms, real time): true if it is to be drawn. */
  admit(now: number): boolean {
    const interval = now - this.last;
    this.last = now;
    // Gaps of a hidden page or a stalled thread say nothing about the screen.
    if (interval > 0 && interval < 100) {
      this.intervals[this.recorded++ % PACE_WINDOW] = interval;
      if (this.recorded % PACE_WINDOW === 0) this.measure();
    }
    if (this.every > 1 && now - this.lastDrawn < (this.every - 0.5) * this.refresh) return false;
    this.lastDrawn = now;
    return true;
  }

  /** Draws every this many refreshes of the screen (1: every one). */
  get divisor(): number {
    return this.every;
  }

  private measure(): void {
    const sorted = Array.from(this.intervals).sort((a, b) => a - b);
    this.refresh = sorted[PACE_WINDOW >> 1]!;
    const fast = this.refresh >= FASTEST_SCREEN_MS && this.refresh < FAST_SCREEN_MS;
    this.every = fast ? Math.max(1, Math.round(PACED_INTERVAL_MS / this.refresh)) : 1;
  }
}

export class FrameLoop {
  private handle = 0;
  private active = false;

  constructor(
    private readonly onFrame: (now: number) => void,
    private readonly clock: () => number,
    private readonly scheduler: FrameScheduler = animationFrames,
    /** Skips frames on fast screens (see FramePacer); null draws every frame. */
    private readonly pacer: FramePacer | null = null,
    /**
     * Real time for the pacer when the scheduler gives no frame time (the engine clock may be
     * warped by the dev harness).
     */
    private readonly realTime: () => number = clock,
  ) {}

  get running(): boolean {
    return this.active;
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    this.handle = this.scheduler.request(this.tick);
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.scheduler.cancel(this.handle);
  }

  private readonly tick = (time?: number): void => {
    if (!this.active) return;
    // Ask for the next frame first: an exception in one frame must not stop the game.
    this.handle = this.scheduler.request(this.tick);
    // The frame's own time (vsync) is steadier than the moment its callback runs.
    if (this.pacer && !this.pacer.admit(time ?? this.realTime())) return;
    this.onFrame(this.clock());
  };
}
